import React from "react";
import { House, ChatCircleText, ChartLineUp, User } from "@phosphor-icons/react";
import { hapticTap } from "../../lib/haptics";

/**
 * MobileBottomNav — barre de navigation basse pour mobile (portrait uniquement).
 * Affichée sur le portail téléprospection en dessous de sm (< 640 px).
 *
 * Chaque bouton scrolle vers une ancre de la page ou déclenche une action.
 * Le lien "Aide IA" ouvre le bilan de journée si un télépro clique en fin de tournée.
 *
 * Props:
 *   - active: string — id actif (hint visuel)
 *   - onOpenEndOfDay: () => void
 *   - hasToday: bool — s'il y a une liste à traiter aujourd'hui
 */
export default function MobileBottomNav({ active = "today", onOpenEndOfDay, hasToday = false }) {
  const goto = (anchor) => {
    hapticTap();
    const el = document.querySelector(anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const items = [
    {
      id: "today",
      icon: <House size={20} weight={active === "today" ? "fill" : "duotone"}/>,
      label: "Appels",
      onClick: () => goto("[data-testid='telepro-today-block']"),
    },
    {
      id: "kpis",
      icon: <ChartLineUp size={20} weight={active === "kpis" ? "fill" : "duotone"}/>,
      label: "KPIs",
      onClick: () => goto("[data-testid='telepro-kpis']"),
    },
    {
      id: "ai",
      icon: <ChatCircleText size={20} weight={active === "ai" ? "fill" : "duotone"}/>,
      label: "Bilan IA",
      onClick: () => { hapticTap(); onOpenEndOfDay?.(); },
      disabled: !hasToday,
    },
    {
      id: "account",
      icon: <User size={20} weight={active === "account" ? "fill" : "duotone"}/>,
      label: "Compte",
      onClick: () => goto("#contract-section"),
    },
  ];

  return (
    <nav
      className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur-md border-t border-border shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="mobile-bottom-nav"
      aria-label="Navigation principale"
    >
      <div className="grid grid-cols-4">
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={it.onClick}
            disabled={it.disabled}
            className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[54px] transition-colors ${
              active === it.id
                ? "text-primary"
                : it.disabled
                ? "text-muted-foreground/40"
                : "text-muted-foreground hover:text-foreground active:bg-muted/50"
            }`}
            data-testid={`bottom-nav-${it.id}`}
          >
            {it.icon}
            <span className="text-[10px] font-bold uppercase tracking-wide leading-none">{it.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}
